import { NgClass } from '@angular/common';
import { RouterLink } from '@angular/router';
import { AuthorizationService } from "@app/core/auth/authorization.service";
import { IsBlvDirective } from '@app/core/auth/is-blv.directive';
import { ConfigurationService } from "@app/core/config/configuration.service";
import Swal from "sweetalert2";
import { Component, computed, DestroyRef, inject, OnDestroy, OnInit, ResourceRef, signal } from '@angular/core';
import { AuthenticationService } from '@app/core/auth/authentication.service';
import { Login } from '@app/core/auth/login';
import { LoginService } from '@app/shared/services/login.service';
import { sprachen } from '@app/shared/sprache';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { interval, SubscriptionLike } from 'rxjs';
import { map } from 'rxjs/operators';
import { rxResource, takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  imports: [
    IsBlvDirective,
    NgClass,
    TranslatePipe,
    RouterLink,
  ],
})
export class HeaderComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly personService = inject(LoginService);
  private readonly translate = inject(TranslateService);
  private readonly configurationService = inject(ConfigurationService);

  private backendPingTask?: SubscriptionLike;
  protected readonly stage = this.configurationService.getConfig().stage;
  protected readonly sprachen = sprachen;
  protected readonly currentLanguage = signal(this.translate.getCurrentLang());

  private readonly currentUser: ResourceRef<Login|null> = rxResource({
    defaultValue: null,
    stream: () => this.authenticationService.authenticated$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .pipe(map(() => this.authenticationService.getUser()))
  });

  protected readonly displayName = computed(() => {
    const user = this.currentUser.value();
    if (user?.uid) {
      // IS-ABV
      return user.zusatz ? `${user.uid} - ${user.zusatz}` : user.uid;
    } else if (user) {
      // eIAM
      return [user.vorname, user.nachname].filter(Boolean).join(' ');
    }
    return '';
  })

  ngOnInit() {
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        this.currentLanguage.set(event.lang);
      });
    this.authenticationService.onTokenExpired = () => this.tokenExpired();
    this.backendPingTask = interval(10_000).subscribe(() => {
      this.personService.checkLoginStatus().subscribe(
        loggedIn => loggedIn || this.tokenExpired()
      )
    });
  }

  ngOnDestroy() {
    this.backendPingTask?.unsubscribe();
  }

  tokenExpired() {
    this.backendPingTask?.unsubscribe();
    Swal.fire({
      icon: 'info',
      title: this.translate.instant('global.tokenExpired.title'),
      text: this.authorizationService.isTierarzt() ?
        this.translate.instant('global.tokenExpired.isabv') :
        this.translate.instant('global.tokenExpired.blv'),
      confirmButtonText: this.translate.instant('global.tokenExpired.reload'),
      showConfirmButton: this.authorizationService.isBlv(),
      allowOutsideClick: false,
      allowEscapeKey: false,
      allowEnterKey: false
    }).then(() => location.reload());
  }

  changeLanguage(newLang: string) {
    const user = this.currentUser.value();
    if (!user) {
      throw new Error("Component not initialized yet")
    }
    user.language = newLang;
    this.personService.changeLanguage(user)
      .subscribe(result => {
        user.language = result.language;
        this.translate.use(user.language);
        globalThis.location.reload();
      });
  }

  userAccount() {
    this.authenticationService.goToMyAccount();
  }

  async logout() {
    return this.authenticationService.logout();
  }
}
